import React, {useState} from "react";
import {Layout} from "../../pages/siren/Layout";
import {DriverBillingStat, useGetSirenBillingStats} from "./hooks/useGetSirenBillingStats";
import {groupBy} from "lodash";
import {useSiren} from "../../pages/siren/SirenContext";
import {generateAndSaveXLSXMultiSheet, generateAndSaveXLSXRawSheets} from "./import/useDownloadXLSX";
import {IconButton} from "./import/inputs";
import DownloadIcon from "@mui/icons-material/Download";
import {platformAPI} from "@/api/axiosConfig";

function firstOfMonth(): string {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-01`;
}

function today(): string {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`;
}

function toMinutes(seconds: number): string {
    return (seconds / 60).toFixed(1);
}

function toDateStamp(dateStr: string): string {
    return dateStr.replace(/-/g, "");
}

function toSlug(str: string): string {
    return str.toLowerCase().replace(/[^a-z0-9]+/g, "_").replace(/^_|_$/g, "");
}

const COL_COUNT = 5;
const COL_WIDTHS = ["w-[12%]", "w-[22%]", "w-[34%]", "w-[16%]", "w-[16%]"];

function StatsTable({headers, rows}: { headers: string[], rows: (string | number)[][] }) {
    const paddedHeaders = [...headers, ...Array(COL_COUNT - headers.length).fill("")];
    const totals: (string | number)[] = Array(COL_COUNT).fill("").map((_, col) => {
        const vals = rows.map(r => r[col]).filter(v => typeof v === "number") as number[];
        return vals.length > 0 ? vals.reduce((a, b) => a + b, 0) : "";
    });
    totals[0] = "Total";
    return (
        <div className="overflow-x-auto">
            <table className="w-full table-fixed text-sm">
                <thead>
                <tr className="bg-light-light-grey text-dark-grey text-left">
                    {paddedHeaders.map((h, i) => (
                        <th key={i} className={`px-4 py-2 font-semibold whitespace-nowrap ${COL_WIDTHS[i]}`}>{h}</th>
                    ))}
                </tr>
                </thead>
                <tbody>
                {rows.map((row, i) => {
                    const paddedRow = [...row, ...Array(COL_COUNT - row.length).fill("")];
                    return (
                        <tr key={i} className="border-t border-light-light-grey">
                            {paddedRow.map((cell, j) => (
                                <td key={j} className="px-4 py-2 whitespace-nowrap">{cell}</td>
                            ))}
                        </tr>
                    );
                })}
                <tr className="border-t border-light-light-grey bg-light-light-grey/50 font-semibold">
                    {totals.map((cell, j) => (
                        <td key={j} className="px-4 py-2 whitespace-nowrap">{cell}</td>
                    ))}
                </tr>
                </tbody>
            </table>
        </div>
    );
}

type Section = {label: string, headers: string[], rows: (string | number)[][]}

function buildSections(rows: DriverBillingStat[]): Section[] {
    const smsRows = rows.filter(r => r.smsSent > 0);
    const callRows = rows.filter(r => r.callsSent > 0);
    const emailRows = rows.filter(r => r.emailSent > 0);
    return [
        smsRows.length > 0 && {
            label: "SMS",
            headers: ["Region", "Driver Type", "From", "Sent", "Segments"],
            rows: smsRows.map(r => [r.region || "—", r.driverType, r.fromAddress || "—", r.smsSent, r.smsSegments]),
        },
        callRows.length > 0 && {
            label: "Calls",
            headers: ["Region", "Driver Type", "From", "Calls", "Duration (min)"],
            rows: callRows.map(r => [r.region || "—", r.driverType, r.fromAddress || "—", r.callsSent, toMinutes(r.callSeconds)]),
        },
        emailRows.length > 0 && {
            label: "Email",
            headers: ["Region", "Driver Type", "From", "Sent"],
            rows: emailRows.map(r => [r.region || "—", r.driverType, r.fromAddress || "—", r.emailSent]),
        },
    ].filter(Boolean) as Section[];
}

function ClientStats({rows}: { clientID: string, rows: DriverBillingStat[] }) {
    const sections = buildSections(rows);
    if (sections.length === 0) return null;

    return (
        <div className="flex flex-col gap-4 mb-6">
            {sections.map(section => (
                <div key={section.label} className="bg-white rounded-lg shadow-md overflow-hidden">
                    <p className="px-4 pt-4 pb-2 text-xs font-semibold text-light-grey uppercase tracking-wide">{section.label}</p>
                    <StatsTable headers={section.headers} rows={section.rows}/>
                </div>
            ))}
        </div>
    );
}

export default function SirenBillingStatsPage() {
    const {clientID} = useSiren();
    const [from, setFrom] = useState(firstOfMonth);
    const [to, setTo] = useState(today);

    const {data, isLoading} = useGetSirenBillingStats(from, to, clientID);

    const grouped = groupBy(data ?? [], r => r.clientID);

    function handleExport() {
        const sheets = Object.entries(grouped).flatMap(([, rows]) => {
            return buildSections(rows).map(s => ({
                name: s.label,
                headers: s.headers.map(String),
                rows: s.rows.map(r => r.map(String)),
            }));
        });
        if (sheets.length === 0) return;
        const clientSlug = clientID ? toSlug(clientID) : "all";
        const filename = `${clientSlug}_${toDateStamp(from)}_${toDateStamp(to)}.xlsx`;
        generateAndSaveXLSXMultiSheet(sheets, filename);
    }

    const [isExportingAll, setIsExportingAll] = useState(false);

    async function handleExportByClient() {
        setIsExportingAll(true);
        try {
            // Fetch all stats (no clientID filter) and all siren client names in parallel.
            const [statsRes, firstPageRes] = await Promise.all([
                platformAPI.get<DriverBillingStat[]>("api/siren/billing-stats", {params: {from, to}}),
                platformAPI.get<{items: {id: string; name: string}[]; totalPages: number}>("api/clients", {
                    params: {page: 1, limit: 100, module: "siren"},
                }),
            ]);

            const clientItems = [...firstPageRes.data.items];
            const totalPages = firstPageRes.data.totalPages;
            if (totalPages > 1) {
                const pages = await Promise.all(
                    Array.from({length: totalPages - 1}, (_, i) =>
                        platformAPI.get<{items: {id: string; name: string}[]}>("api/clients", {
                            params: {page: i + 2, limit: 100, module: "siren"},
                        })
                    )
                );
                pages.forEach(p => clientItems.push(...p.data.items));
            }

            const nameMap: Record<string, string> = Object.fromEntries(clientItems.map(c => [c.id, c.name]));
            const allGrouped = groupBy(statsRes.data, r => r.clientID);

            const sheets = Object.entries(allGrouped).flatMap(([cid, rows]) => {
                const sections = buildSections(rows);
                if (sections.length === 0) return [];
                const aoa: (string | number)[][] = [];
                sections.forEach((s, i) => {
                    if (i > 0) aoa.push([]);
                    aoa.push([s.label]);
                    aoa.push(s.headers);
                    s.rows.forEach(r => aoa.push(r));
                    const totals: (string | number)[] = s.headers.map((_, col) => {
                        const nums = s.rows.map(r => r[col]).filter(v => typeof v === "number") as number[];
                        return nums.length > 0 ? nums.reduce((a, b) => a + b, 0) : col === 0 ? "Total" : "";
                    });
                    aoa.push(totals);
                });
                return [{name: toSlug(nameMap[cid] ?? cid).slice(0, 31), rows: aoa}];
            });
            if (sheets.length === 0) return;
            generateAndSaveXLSXRawSheets(sheets, `billing_all_clients_${toDateStamp(from)}_${toDateStamp(to)}.xlsx`);
        } finally {
            setIsExportingAll(false);
        }
    }

    return (
        <Layout menuCurrent="billing-stats">
            <div className="p-6">
                <div className="flex items-center gap-4 mb-6">
                    <h1 className="text-xl font-semibold text-dark-dark-grey">Billing Statistics</h1>
                    <div className="flex items-center gap-2 ml-auto">
                        <label className="text-sm text-dark-grey">From</label>
                        <input
                            type="date"
                            value={from}
                            onChange={e => setFrom(e.target.value)}
                            className="border border-light-grey rounded-md px-3 py-1.5 text-sm"
                        />
                        <label className="text-sm text-dark-grey">To</label>
                        <input
                            type="date"
                            value={to}
                            onChange={e => setTo(e.target.value)}
                            className="border border-light-grey rounded-md px-3 py-1.5 text-sm"
                        />
                    </div>
                </div>

                {isLoading ? (
                    <div className="p-8 text-center text-light-grey text-sm">Loading...</div>
                ) : Object.keys(grouped).length === 0 ? (
                    <div className="p-8 text-center text-light-grey text-sm bg-white rounded-lg shadow-md">
                        No data for the selected date range.
                    </div>
                ) : (
                    <>
                        {Object.entries(grouped).map(([clientID, rows]) => (
                            <ClientStats key={clientID} clientID={clientID} rows={rows}/>
                        ))}
                        <div className="flex justify-end gap-2 mt-2">
                            <IconButton
                                text="Export"
                                icon={<DownloadIcon style={{fontSize: 16}}/>}
                                onClick={handleExport}
                                className="w-32"
                            />
                            <IconButton
                                text={isExportingAll ? "Exporting..." : "Export All Clients"}
                                icon={<DownloadIcon style={{fontSize: 16}}/>}
                                onClick={handleExportByClient}
                                disabled={isExportingAll}
                                className="w-44"
                            />
                        </div>
                    </>
                )}
            </div>
        </Layout>
    );
}
